import axios from 'axios';
import Keycloak from './Keycloak';
import {
  ensureFresh,
  redirectToLogin,
  refreshUnavailable,
  TOKEN_MIN_VALIDITY_SECONDS
} from './authSession';

/**
 * Methods we are willing to send a second time after a 401.
 *
 * Deliberately excludes POST even though some reads here are POSTs - replaying one would re-submit
 * training data. It also keeps uploads out, since those are POSTs carrying FormData and a replay
 * would store a second copy.
 */
const REPLAYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const axiosClient = axios.create({
  baseURL:
    (!window.g_baseUrl?.startsWith('%') ? window.g_baseUrl : null) ??
    process.env.REACT_APP_BASE_URL ??
    '/ck',
  timeout: 300000
});

axiosClient.interceptors.request.use(
  async (config) => {
    // Refresh first, attach the token second - reading Keycloak.token up front would send the
    // expired one. This is a no-op while the token has more than the threshold left, so on an
    // 8 hour lifespan it never reaches Keycloak.
    try {
      await ensureFresh(TOKEN_MIN_VALIDITY_SECONDS);
    } catch {
      // The session is gone and the login redirect is already under way. Let the request go out and
      // come back as an ordinary 401 rather than rejecting here: a rejected request interceptor
      // produces a bare Error with no `response`, and useAlert would put that object straight into
      // the alert, which React refuses to render.
    }

    // Skipped when the token has been cleared, so we never send a literal "Bearer undefined".
    if (Keycloak.token) {
      config.headers['Authorization'] = `Bearer ${Keycloak.token}`;
    }

    // Only for bodies that really are JSON - forcing it on FormData would strip the multipart
    // boundary and break the upload.
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = `application/json`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const original = error.config;

    // A 401 here means the backend rejected a token we considered fresh - clock skew, or the
    // session was revoked server side. Anything else, 403 included, is left to the caller: that is
    // about permissions rather than authentication, so refreshing cannot help.
    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(error);
    }

    // Forcing a refresh is safe whatever the request was, because it re-sends nothing.
    try {
      await ensureFresh(-1);
    } catch {
      // Session really is gone - the login redirect is already under way.
      return Promise.reject(error);
    }

    // Keycloak is unreachable, so the refresh above was skipped and the token is unchanged.
    // Replaying would send the same stale token, and redirecting would land the user on a login page
    // served by that same unreachable Keycloak - losing the app and any unsaved work for no reason.
    // Surface the 401 instead; once the cooldown expires the next request retries the refresh on its
    // own, so this recovers without a reload.
    if (refreshUnavailable()) {
      return Promise.reject(error);
    }

    // Replaying is not safe in general: a 401 can arrive after the backend already applied the
    // change. Requests we will not replay surface the 401, and the refresh above means the user's
    // next attempt succeeds.
    if (!REPLAYABLE_METHODS.has((original.method ?? '').toUpperCase())) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      return await axiosClient(original);
    } catch (retryError) {
      // Still 401 with a token minted seconds ago: this is not going to start working.
      if (retryError.response?.status === 401) {
        redirectToLogin();
      }
      return Promise.reject(retryError);
    }
  }
);

export default axiosClient;
